import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Drawer
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as ExportIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable, LoadingSpinner, DetailCard } from '@/components';
import type { DetailSection } from '@/components/DetailCard/DetailCard';
import {
  ManagementPageProps,
  BaseEntity,
} from '@/models/management';
import { ApiResponse } from '@/models/common';
import type { Column as DataTableColumn, Action as DataTableAction } from '@/components/DataTable/DataTable';
import { useDataManager } from '@/hooks/useDataManager';
import { processData } from '@/utils/dataUtils';
import './ManagementPage.css';
import FormField from '@/components/FormField/FormField';

const ManagementPage = <T extends BaseEntity, CreateData, UpdateData = CreateData>({
  config,
  user,
  onLogout,
  darkMode,
  setDarkMode
}: ManagementPageProps<T, CreateData, UpdateData> & {
  user: any;
  onLogout: () => void;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}) => {
  const navigate = useNavigate();

  // Memoize the fetch data function to prevent infinite loops
  const fetchData = useCallback(async (): Promise<T[]> => {
    const token = localStorage.getItem('jwt');
    if (!token) {
      console.error('No JWT token found');
      throw new Error('Authentication required. Please login.');
    }

    const response: ApiResponse<T[]> = await config.service.getItems();

    if (response.success) {
      return response.data;
    } else {
      console.error('API returned error:', response.message || response.error);
      throw new Error(`Failed to load ${config.entityNamePlural}`);
    }
  }, [config.service, config.entityNamePlural]);

  // Use the data manager hook for client-side filtering, sorting, and pagination
  const {
    allData,
    filteredData,
    paginatedData,
    filters,
    updateFilter,
    loading,
    refreshData,
    error
  } = useDataManager(
    fetchData,
    {
      searchFields: config.searchFields || ['name', 'id'] as (keyof T)[],
      defaultPage: 1,
      defaultLimit: 50,
      defaultSortBy: config.defaultSortBy,
      defaultSortOrder: config.defaultSortOrder
    }
  );

  // State management
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [viewingItem, setViewingItem] = useState<T | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateData>({} as CreateData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Reset to page 1 when status filter changes
  useEffect(() => {
    updateFilter('page', 1);
  }, [selectedStatus]);

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({ open: true, message, severity });
  };

  const isInitialLoading = loading && allData.length === 0;

  // Handle search (client-side)
  const handleSearch = (searchTerm: string) => {
    updateFilter('search', searchTerm);
  };

  // Handle sort (client-side)
  const handleSort = (field: keyof T, order: 'asc' | 'desc') => {
    updateFilter('sortBy', field);
    updateFilter('sortOrder', order);
  };

  // Handle pagination (client-side)
  const handlePageChange = (newPage: number) => {
    updateFilter('page', newPage + 1); // Convert to 1-based indexing
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    updateFilter('limit', newRowsPerPage);
    updateFilter('page', 1);
  };

  // Handle selection
  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids);
  };

  // Initialize form data
  const initializeFormData = useCallback(() => {
    const initialData: any = {};
    config.createFormFields.forEach(field => {
      if (field.name === 'unit') {
        initialData[field.name] = 'pc';
      } else if (field.name === 'gst_percentage') {
        initialData[field.name] = 18;
      } else if (field.name === 'type') {
        initialData[field.name] = 'PRODUCT';
      } else if (field.name === 'added_by' || field.name === 'created_by') {
        initialData[field.name] = user?.username || 'ADMIN';
      } else if (field.type === 'number') {
        initialData[field.name] = 0;
      } else {
        initialData[field.name] = '';
      }
    });
    return initialData as CreateData;
  }, [config.createFormFields, user]);

  // Open create — navigate if createRoute defined
  const handleCreate = () => {
    if (config.createRoute) {
      navigate(config.createRoute);
      return;
    }
    setEditingItem(null);
    setFormData(initializeFormData());
    setFormErrors({});
    setDrawerOpen(true);
  };

  // Open edit drawer
  const handleEdit = (item: T) => {
    setEditingItem(item);
    const editData: any = {};
    config.editFormFields.forEach(field => {
      editData[field.name] = (item as any)[field.name] || '';
    });
    setFormData(editData as CreateData);
    setFormErrors({});
    setDrawerOpen(true);
  };

  // Open view drawer
  const handleView = (item: T) => {
    setViewingItem(item);
  };

  // Handle delete
  const handleDelete = (item: T) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setSubmitting(true);
    try {
      const response = await config.service.deleteItem(itemToDelete._id);

      if (response.success) {
        showNotification(`${config.entityName} deleted successfully`, 'success');
        refreshData();
        setSelectedIds(selectedIds.filter(id => id !== itemToDelete._id));
      } else {
        showNotification(`Failed to delete ${config.entityName}`, 'error');
      }
    } catch (error) {
      console.error(`Error deleting ${config.entityName}:`, error);
      showNotification(`Failed to delete ${config.entityName}`, 'error');
    } finally {
      setSubmitting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const errors = editingItem
      ? config.validation.edit(formData as unknown as UpdateData)
      : config.validation.create(formData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      let response;

      if (editingItem) {
        response = await config.service.updateItem(editingItem._id, formData as unknown as UpdateData);
      } else {
        response = await config.service.createItem(formData);
      }

      if (response.success) {
        showNotification(
          editingItem
            ? `${config.entityName} updated successfully`
            : `${config.entityName} created successfully`,
          'success'
        );
        setDrawerOpen(false);
        refreshData();
      } else {
        showNotification(`Failed to save ${config.entityName}`, 'error');
      }
    } catch (error) {
      console.error(`Error saving ${config.entityName}:`, error);
      showNotification(`Failed to save ${config.entityName}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle export (client-side)
  const handleExport = async () => {
    try {
      const exportData = processData(allData, filters, config.searchFields || ['name', 'id'] as (keyof T)[]);

      const headers = config.columns.reduce((acc, col) => {
        acc[col.id as keyof T] = col.label;
        return acc;
      }, {} as Record<keyof T, string>);

      const csvContent = [
        Object.values(headers).join(','),
        ...exportData.data.map(item =>
          config.columns.map(col => {
            const value = (item as any)[col.id];
            if (col.format) {
              const formatted = col.format(value, item);
              return typeof formatted === 'string' ? formatted : value;
            }
            return value || '';
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.entityNamePlural}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification(`${config.entityNamePlural} exported successfully`, 'success');
    } catch (error) {
      console.error(`Error exporting ${config.entityNamePlural}:`, error);
      showNotification(`Failed to export ${config.entityNamePlural}`, 'error');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    setSubmitting(true);
    try {
      const response = await config.service.bulkDeleteItems(selectedIds);

      if (response.success) {
        showNotification(`${response.data.deletedCount} ${config.entityNamePlural} deleted successfully`, 'success');
        setSelectedIds([]);
        refreshData();
      } else {
        showNotification(`Failed to delete ${config.entityNamePlural}`, 'error');
      }
    } catch (error) {
      console.error(`Error bulk deleting ${config.entityNamePlural}:`, error);
      showNotification(`Failed to delete ${config.entityNamePlural}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'ViewIcon': return <ViewIcon />;
      case 'EditIcon': return <EditIcon />;
      case 'DeleteIcon': return <DeleteIcon />;
      case 'PrintIcon': return <PrintIcon />;
      default: return <ViewIcon />;
    }
  };

  // Convert config columns to DataTable columns
  const columns: DataTableColumn<T>[] = useMemo(() => {
    return config.columns.map(col => ({
      id: col.id as keyof T,
      label: col.label,
      minWidth: col.minWidth,
      align: col.align,
      sortable: col.sortable,
      format: col.format ? (value: any) => {
        const result = col.format!(value, {} as T);
        if (result && typeof result === 'object' && 'label' in result && 'color' in result) {
          const chipProps = result as { label: string; color: string; size?: string; className?: string };
          return (
            <Chip
              label={chipProps.label}
              color={chipProps.color as any}
              size={chipProps.size as any || 'small'}
              className={chipProps.className || 'status-chip'}
            />
          );
        }
        return result;
      } : undefined
    }));
  }, [config.columns]);

  // Convert config actions to DataTable actions
  const actions: DataTableAction<T>[] = useMemo(() => {
    const isPlaceholderAction = (fn?: ((item: T) => void) | null) => {
      if (!fn) return true;
      const body = fn.toString().replace(/\s/g, '');
      return body === '() => {}' || body === 'function(){}';
    };

    return config.actions.map(action => {
      let onClick = action.onClick;

      if (isPlaceholderAction(onClick)) {
        if (action.label === 'Edit') {
          onClick = (item: T) => handleEdit(item);
        } else if (action.label === 'View') {
          onClick = (item: T) => handleView(item);
        } else if (action.label === 'Delete') {
          onClick = (item: T) => handleDelete(item);
        }
      }

      return {
        label: action.label,
        icon: getIconComponent(action.icon as string),
        onClick,
        color: action.color
      };
    });
  }, [config.actions, handleEdit, handleView, handleDelete]);

  // Status-filtered data (applied on top of search/sort filtered data)
  const statusFilteredData = useMemo(() => {
    if (!config.statusFilter || selectedStatus === null) return filteredData;
    return filteredData.filter(
      (item: any) => item[config.statusFilter!.field] === selectedStatus
    );
  }, [filteredData, selectedStatus, config.statusFilter]);

  const countByStatus = (status: string) =>
    allData.filter((item: any) => item[config.statusFilter!.field] === status).length;

  // Render form field
  const renderFormField = (field: any) => {
    const value = (formData as any)[field.name] || '';
    const error = formErrors[field.name];

    return (
      <FormField
        field={field}
        value={value}
        onChange={(newValue) => setFormData({ ...formData, [field.name]: newValue })}
        error={error}
        disabled={submitting}
        compact={true}
      />
    );
  };

  // Convert view fields to DetailCard format
  const convertToDetailSections = (item: T): DetailSection[] => {
    const sections: DetailSection[] = [];

    const basicFields = config.viewFields.filter(field =>
      ['id', 'name', 'item_code', 'item_name', 'username', 'email'].includes(field.name)
    );
    const contactFields = config.viewFields.filter(field =>
      ['tel', 'mobile', 'fax', 'email'].includes(field.name)
    );
    const addressFields = config.viewFields.filter(field =>
      ['address_1', 'address_2', 'address_3', 'address_4', 'pin'].includes(field.name)
    );
    const taxFields = config.viewFields.filter(field =>
      ['gst_id', 'panno', 'localoutside', 'hsn_code', 'gst_percentage'].includes(field.name)
    );
    const systemFields = config.viewFields.filter(field =>
      ['created_by', 'updated_by', 'created_on', 'updated_on', 'createdAt', 'updatedAt', 'role'].includes(field.name)
    );
    const productFields = config.viewFields.filter(field =>
      ['rate', 'type', 'unit'].includes(field.name)
    );

    if (basicFields.length > 0) {
      sections.push({
        title: 'Basic Information',
        fields: basicFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name === 'localoutside' ? 'chip' : 'text',
          chipType: (item as any)[field.name] === 'Local' ? 'local' : 'outside'
        }))
      });
    }
    if (contactFields.length > 0) {
      sections.push({
        title: 'Contact Information',
        fields: contactFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name === 'tel' || field.name === 'mobile' || field.name === 'fax' ? 'phone' : 'text'
        }))
      });
    }
    if (addressFields.length > 0) {
      sections.push({
        title: 'Address Information',
        fields: addressFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name.includes('address') ? 'address' : 'text',
          fullWidth: field.name.includes('address')
        }))
      });
    }
    if (taxFields.length > 0) {
      sections.push({
        title: 'Tax Information',
        fields: taxFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name === 'localoutside' ? 'chip' :
                field.name === 'gst_percentage' ? 'percentage' : 'text',
          chipType: (item as any)[field.name] === 'Local' ? 'local' : 'outside'
        }))
      });
    }
    if (productFields.length > 0) {
      sections.push({
        title: 'Product Details',
        fields: productFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name === 'rate' ? 'currency' : 'text'
        }))
      });
    }
    if (systemFields.length > 0) {
      sections.push({
        title: 'System Information',
        fields: systemFields.map(field => ({
          name: field.name,
          label: field.label,
          value: (item as any)[field.name] || '-',
          type: field.name.includes('created') || field.name.includes('updated') ? 'date' :
                field.name === 'created_by' || field.name === 'updated_by' ? 'chip' : 'text',
          chipType: (item as any)[field.name] === 'ADMIN' ? 'admin' : 'user'
        }))
      });
    }

    return sections;
  };

  const isCustomerForm = config.entityName === 'Customer';
  const isProductForm = config.entityName === 'Product';

  // Paginate the status-filtered data manually
  const page = filters.page || 1;
  const limit = filters.limit || 50;
  const pagedStatusData = statusFilteredData.slice((page - 1) * limit, page * limit);

  if (isInitialLoading) {
    return (
      <Box className="management-page-loading" sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner message={`Loading ${config.entityNamePlural}...`} />
      </Box>
    );
  }

  return (
    <Box className="management-page">
      <Box className="management-content">

        {/* Page header */}
        <Box className="mgmt-header">
          <Box className="mgmt-header-left">
            <Typography variant="h5" className="mgmt-title">
              {config.entityNamePlural}
            </Typography>
            <Typography variant="caption" className="mgmt-count">
              {paginatedData.total} records
            </Typography>
          </Box>
          <Box className="mgmt-header-right">
            <Tooltip title="Refresh">
              <IconButton onClick={refreshData} disabled={loading} size="small" className="mgmt-icon-btn">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {config.features.export && (
              <Tooltip title="Export CSV">
                <IconButton onClick={handleExport} size="small" className="mgmt-icon-btn">
                  <ExportIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {config.features.create && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                size="small"
                className="mgmt-add-btn"
              >
                Add {config.entityName}
              </Button>
            )}
          </Box>
        </Box>

        {/* Status filter chips (invoices only) */}
        {config.statusFilter && (
          <Box className="status-filter-bar">
            {config.statusFilter.options.map((opt) => (
              <Chip
                key={opt.label}
                label={
                  opt.value === null
                    ? `${opt.label} ${allData.length}`
                    : `${opt.label} ${countByStatus(opt.value)}`
                }
                onClick={() => setSelectedStatus(opt.value)}
                color={selectedStatus === opt.value ? (opt.color || 'primary') : 'default'}
                variant={selectedStatus === opt.value ? 'filled' : 'outlined'}
                size="small"
                className="status-filter-chip"
              />
            ))}
          </Box>
        )}

        {/* Data Table */}
        <DataTable<T>
          columns={columns}
          data={pagedStatusData}
          total={statusFilteredData.length}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSearch={config.features.search ? handleSearch : undefined}
          onSort={config.features.sort ? handleSort : undefined}
          actions={actions}
          selectable={config.features.bulkDelete}
          onSelectionChange={handleSelectionChange}
          selectedIds={selectedIds}
          loading={loading}
          error={error}
          searchPlaceholder={`Search ${config.entityNamePlural}...`}
          showExport={false}
          title=""
          subtitle=""
        />

      </Box>

      {/* Bulk Actions */}
      {config.features.bulkDelete && selectedIds.length > 0 && (
        <Box className="bulk-actions">
          <Typography variant="body2" className="bulk-actions-text">
            {selectedIds.length} {config.entityName}(s) selected
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={submitting}
            size="small"
          >
            Delete Selected
          </Button>
        </Box>
      )}

      {/* Right slide-over Drawer for Create/Edit */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 420 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer header */}
        <Box className="drawer-header">
          <Box>
            <Typography variant="subtitle1" className="drawer-title">
              {editingItem ? `Edit ${config.entityName}` : `Add ${config.entityName}`}
            </Typography>
            <Typography variant="caption" className="drawer-subtitle">
              {editingItem
                ? `Update ${config.entityName} information`
                : `Create a new ${config.entityName} record`}
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Drawer body */}
        <Box className="drawer-body">
          <Box className="form-container">
            <Grid container spacing={2}>
              {(editingItem ? config.editFormFields : config.createFormFields).map((field) => (
                <Grid
                  item
                  key={field.name}
                  xs={field.gridSize?.xs || 12}
                  sm={field.gridSize?.sm || 6}
                  md={field.gridSize?.md || 6}
                >
                  {renderFormField(field)}
                </Grid>
              ))}
            </Grid>

            {/* Customer-specific address/contact fields */}
            {isCustomerForm && (
              <>
                <Box className="form-section-divider">
                  <Typography variant="caption" className="form-section-label">Address</Typography>
                </Box>
                <Grid container spacing={2}>
                  {['address_1', 'address_2', 'address_3', 'address_4'].map((fieldName, idx) => (
                    <Grid item xs={12} key={fieldName}>
                      <FormField
                        field={{
                          name: fieldName,
                          label: `Address Line ${idx + 1}`,
                          type: 'text',
                          required: idx === 0,
                          placeholder: idx === 0 ? 'Enter primary address' : 'Optional'
                        }}
                        value={(formData as any)[fieldName] || ''}
                        onChange={(value) => setFormData({ ...formData, [fieldName]: value })}
                        error={formErrors[fieldName]}
                        disabled={submitting}
                        compact={true}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <FormField
                      field={{ name: 'pin', label: 'PIN Code', type: 'text', required: true, placeholder: '123456' }}
                      value={(formData as any).pin || ''}
                      onChange={(value) => setFormData({ ...formData, pin: value })}
                      error={formErrors.pin}
                      disabled={submitting}
                      compact={true}
                    />
                  </Grid>
                </Grid>

                <Box className="form-section-divider">
                  <Typography variant="caption" className="form-section-label">Contact</Typography>
                </Box>
                <Grid container spacing={2}>
                  {[
                    { name: 'tel', label: 'Telephone', type: 'phone' as const },
                    { name: 'mobile', label: 'Mobile', type: 'phone' as const },
                    { name: 'fax', label: 'Fax', type: 'text' as const },
                  ].map((f) => (
                    <Grid item xs={12} sm={4} key={f.name}>
                      <FormField
                        field={{ name: f.name, label: f.label, type: f.type, required: f.name === 'tel' || f.name === 'mobile', placeholder: 'Enter number' }}
                        value={(formData as any)[f.name] || ''}
                        onChange={(value) => setFormData({ ...formData, [f.name]: value })}
                        error={formErrors[f.name]}
                        disabled={submitting}
                        compact={true}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        </Box>

        {/* Drawer footer */}
        <Box className="drawer-footer">
          <Button onClick={() => setDrawerOpen(false)} disabled={submitting} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            size="small"
          >
            {editingItem ? 'Save Changes' : `Create ${config.entityName}`}
          </Button>
        </Box>
      </Drawer>

      {/* View Drawer */}
      <Drawer
        anchor="right"
        open={!!viewingItem}
        onClose={() => setViewingItem(null)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box className="drawer-header">
          <Box>
            <Typography variant="subtitle1" className="drawer-title">
              {config.entityName} Details
            </Typography>
          </Box>
          <IconButton onClick={() => setViewingItem(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box className="drawer-body">
          {viewingItem && (
            <DetailCard
              id={(viewingItem as any).id?.toString() || (viewingItem as any).name || 'N/A'}
              subtitle={(viewingItem as any).name || (viewingItem as any).item_name || ''}
              sections={convertToDetailSections(viewingItem)}
              onEdit={() => {
                setViewingItem(null);
                handleEdit(viewingItem);
              }}
              onDelete={() => {
                setViewingItem(null);
                handleDelete(viewingItem);
              }}
              showActions={config.features.edit || config.features.delete}
              status="active"
            />
          )}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {config.entityName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={submitting}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {loading && <LoadingSpinner />}
    </Box>
  );
};

export default ManagementPage;
